import React, { useState } from 'react';
import { toast } from 'react-hot-toast';
import { supabase } from '../../lib/supabase';
import { NativeDateInput, NativeTimeInput } from '../ui/NativeDateInput';
import { useAIService } from '../../services/aiService.simplified';

interface CoachSteveGamePlanFormProps {
  teamId: string;
  teamName: string;
  onSuccess: (result: { id: string; label: string; href: string }) => void;
  onCancel: () => void;
}

export const CoachSteveGamePlanForm: React.FC<CoachSteveGamePlanFormProps> = ({
  teamId,
  teamName,
  onSuccess,
  onCancel,
}) => {
  const { generateGamePlan } = useAIService();
  const [opponentName, setOpponentName] = useState('');
  const [gameDate, setGameDate] = useState('');
  const [gameTime, setGameTime] = useState('');
  const [location, setLocation] = useState('');
  const [homeAway, setHomeAway] = useState<'home' | 'away'>('home');
  const [keyMatchups, setKeyMatchups] = useState('');
  const [opponentStrengths, setOpponentStrengths] = useState('');
  const [opponentWeaknesses, setOpponentWeaknesses] = useState('');
  const [specialConsiderations, setSpecialConsiderations] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!opponentName.trim() || !gameDate) {
      toast.error('Please enter opponent and game date');
      return;
    }
    setIsSubmitting(true);
    try {
      const prompt = `
        Generate a detailed game plan strategy for ${teamName} based on the following information:

        Opponent Analysis:
        • Opponent: ${opponentName}
        • Location: ${location || 'Not specified'}
        • Game Date: ${gameDate}
        • Game Time: ${gameTime || 'Not specified'}
        • Home/Away: ${homeAway}
        • Key Matchups: ${keyMatchups || 'Not specified'}
        • Special Considerations: ${specialConsiderations || 'Not specified'}
        • Opponent Strengths: ${opponentStrengths || 'Not specified'}
        • Opponent Weaknesses: ${opponentWeaknesses || 'Not specified'}

        Please provide a detailed strategy in JSON format with the following structure:
        {
          "offenseStrategy": {
            "overview": "string",
            "keyPoints": ["string"],
            "playRecommendations": [{
              "playType": "string",
              "formation": "string",
              "personnel": "string",
              "situation": "string",
              "notes": "string"
            }],
            "gameSituation": {
              "redZone": "string",
              "shortYardage": "string",
              "twoMinute": "string"
            }
          },
          "defenseStrategy": {
            "overview": "string",
            "keyPoints": ["string"],
            "alignmentRecommendations": [{
              "formation": "string",
              "front": "string",
              "coverage": "string",
              "situation": "string"
            }],
            "blitzPackages": [{
              "type": "string",
              "situation": "string",
              "effectiveness": "string"
            }],
            "gameSituation": {
              "redZone": "string",
              "shortYardage": "string",
              "twoMinute": "string"
            }
          }
        }

        Guidelines:
        1. Focus on exploiting opponent weaknesses while neutralizing their strengths
        2. Consider the key matchups and special considerations provided
        3. Provide specific, actionable strategies
        4. Include situational strategies for different game scenarios
        5. Ensure all responses are detailed and practical
      `;

      toast.loading('Generating game plan...', { id: 'coach-steve-gameplan' });
      const rawStrategies = await generateGamePlan(prompt);
      const parsed = JSON.parse(rawStrategies);

      const gamePlanRecord: Record<string, unknown> = {
        team_id: teamId,
        opponent_name: opponentName.trim(),
        game_date: gameDate,
        game_time: gameTime || null,
        location: location.trim() || null,
        home_away: homeAway,
        key_matchups: keyMatchups.trim() || null,
        special_considerations: specialConsiderations.trim() || null,
        offensive_strategy: parsed.offenseStrategy ?? null,
        defensive_strategy: parsed.defenseStrategy ?? null,
        status: 'completed',
        updated_at: new Date().toISOString(),
        created_at: new Date().toISOString(),
      };

      const { data, error } = await supabase
        .from('game_plans')
        .insert(gamePlanRecord)
        .select('id')
        .single();

      toast.dismiss('coach-steve-gameplan');
      if (error) throw error;
      toast.success('Game plan created!');
      onSuccess({
        id: data.id,
        label: `vs ${opponentName}`,
        href: '/game-plans',
      });
    } catch (err) {
      toast.dismiss('coach-steve-gameplan');
      console.error(err);
      toast.error(err instanceof Error ? err.message : 'Failed to create game plan');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <h4 className="font-semibold text-dark">New game plan</h4>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-stone-700 mb-1">Opponent *</label>
          <input
            type="text"
            value={opponentName}
            onChange={(e) => setOpponentName(e.target.value)}
            placeholder="e.g. Lincoln High"
            className="w-full rounded-lg border border-stone-300 px-3 py-2 text-sm focus:ring-2 focus:ring-primary focus:border-primary"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-stone-700 mb-1">Game date *</label>
          <NativeDateInput
            value={gameDate}
            onChange={(e) => setGameDate(e.target.value)}
            className="w-full rounded-lg border border-stone-300 px-3 py-2 text-sm focus:ring-2 focus:ring-primary focus:border-primary"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-stone-700 mb-1">Game time</label>
          <NativeTimeInput
            value={gameTime}
            onChange={(e) => setGameTime(e.target.value)}
            className="w-full rounded-lg border border-stone-300 px-3 py-2 text-sm focus:ring-2 focus:ring-primary focus:border-primary"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-stone-700 mb-1">Home / Away</label>
          <div className="flex gap-4 mt-1">
            <label className="flex items-center gap-1.5 cursor-pointer">
              <input
                type="radio"
                name="home_away"
                value="home"
                checked={homeAway === 'home'}
                onChange={() => setHomeAway('home')}
                className="text-primary border-stone-300 focus:ring-primary"
              />
              <span className="text-sm">Home</span>
            </label>
            <label className="flex items-center gap-1.5 cursor-pointer">
              <input
                type="radio"
                name="home_away"
                value="away"
                checked={homeAway === 'away'}
                onChange={() => setHomeAway('away')}
                className="text-primary border-stone-300 focus:ring-primary"
              />
              <span className="text-sm">Away</span>
            </label>
          </div>
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-stone-700 mb-1">Location (venue)</label>
        <input
          type="text"
          value={location}
          onChange={(e) => setLocation(e.target.value)}
          placeholder="Stadium or field"
          className="w-full rounded-lg border border-stone-300 px-3 py-2 text-sm focus:ring-2 focus:ring-primary focus:border-primary"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-stone-700 mb-1">Key matchups</label>
        <textarea
          value={keyMatchups}
          onChange={(e) => setKeyMatchups(e.target.value)}
          placeholder="e.g. Our WR vs their CB..."
          className="w-full rounded-lg border border-stone-300 px-3 py-2 text-sm focus:ring-2 focus:ring-primary focus:border-primary"
          rows={2}
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-stone-700 mb-1">Opponent strengths</label>
        <textarea
          value={opponentStrengths}
          onChange={(e) => setOpponentStrengths(e.target.value)}
          placeholder="e.g. Strong run game, physical OL"
          className="w-full rounded-lg border border-stone-300 px-3 py-2 text-sm focus:ring-2 focus:ring-primary focus:border-primary"
          rows={2}
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-stone-700 mb-1">Opponent weaknesses</label>
        <textarea
          value={opponentWeaknesses}
          onChange={(e) => setOpponentWeaknesses(e.target.value)}
          placeholder="e.g. Slow in secondary, struggle vs play action"
          className="w-full rounded-lg border border-stone-300 px-3 py-2 text-sm focus:ring-2 focus:ring-primary focus:border-primary"
          rows={2}
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-stone-700 mb-1">Special considerations</label>
        <textarea
          value={specialConsiderations}
          onChange={(e) => setSpecialConsiderations(e.target.value)}
          placeholder="Weather, injuries, rivalry, etc."
          className="w-full rounded-lg border border-stone-300 px-3 py-2 text-sm focus:ring-2 focus:ring-primary focus:border-primary"
          rows={2}
        />
      </div>
      <div className="flex gap-2 pt-2">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-sm font-medium text-stone-600 hover:text-stone-800"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isSubmitting}
          className="px-4 py-2 text-sm font-medium rounded-lg bg-primary text-white hover:bg-primary/90 disabled:opacity-50"
        >
          {isSubmitting ? 'Generating…' : 'Create game plan'}
        </button>
      </div>
    </form>
  );
};
